/**
 * The settings area: which sections exist, how they group on the index, where each one routes, and
 * how its status is shown.
 *
 * Product decision: docs/features/settings/ia-decision.md. Order follows what a trainer is trying to
 * do — set up the public page, then what is offered and at what price, then the rules, then getting
 * paid — with access and account last. Every row on the settings index and every sub-item under
 * Uzstādījumi in the drawer is driven from this list, so adding a section is one entry here plus its
 * page and locale keys — nothing in the views changes.
 *
 * Locale keys are derived from the id: `mypages.settings.sections.<id>.title` / `.description`.
 */
import { MyPagesRouteName } from '@/router/myPagesPaths'

/** Headings on the settings index. Locale: `mypages.settings.index.groups.<id>`. */
export const SettingsGroup = {
  /** How the studio works and what it sells. */
  STUDIO: 'studio',
  /** Who gets in and what ASport itself costs. */
  ACCESS: 'access'
}

export const SETTINGS_GROUP_ORDER = [SettingsGroup.STUDIO, SettingsGroup.ACCESS]

export const SettingsSectionId = {
  PAGE: 'page',
  SERVICES_PRICING: 'services-pricing',
  GENERAL_RULES: 'general-rules',
  ONLINE_PAYMENTS: 'online-payments',
  TEAM: 'team',
  ACCOUNT: 'account'
}

/**
 * How the index row shows the section's state.
 * CONNECTION — on/off from whether a payment provider is connected; the page does the connecting.
 */
export const SettingsStatusKind = {
  CONNECTION: 'connection'
}

/**
 * @typedef {Object} SettingsSection
 * @property {string} id            SettingsSectionId
 * @property {string} group         SettingsGroup
 * @property {string} icon          mdi icon name
 * @property {string} routeName     MyPagesRouteName of the section page
 * @property {string} [statusKind]  SettingsStatusKind; omitted = no on/off state
 */

/** @type {SettingsSection[]} — index and submenu order. */
export const SETTINGS_SECTIONS = [
  { id: SettingsSectionId.PAGE, group: SettingsGroup.STUDIO, icon: 'mdi-palette-outline', routeName: MyPagesRouteName.MY_SITE },
  { id: SettingsSectionId.SERVICES_PRICING, group: SettingsGroup.STUDIO, icon: 'mdi-tag-multiple-outline', routeName: MyPagesRouteName.SETTINGS_SERVICES_PRICING },
  { id: SettingsSectionId.GENERAL_RULES, group: SettingsGroup.STUDIO, icon: 'mdi-clipboard-text-outline', routeName: MyPagesRouteName.SETTINGS_GENERAL_RULES },
  { id: SettingsSectionId.ONLINE_PAYMENTS, group: SettingsGroup.STUDIO, icon: 'mdi-credit-card-outline', routeName: MyPagesRouteName.SETTINGS_ONLINE_PAYMENTS, statusKind: SettingsStatusKind.CONNECTION },
  { id: SettingsSectionId.TEAM, group: SettingsGroup.ACCESS, icon: 'mdi-account-multiple-outline', routeName: MyPagesRouteName.SETTINGS_TEAM },
  { id: SettingsSectionId.ACCOUNT, group: SettingsGroup.ACCESS, icon: 'mdi-receipt-text-outline', routeName: MyPagesRouteName.SETTINGS_ACCOUNT }
]

/** Route names that belong to the settings area (index + every section page). */
export const SETTINGS_ROUTE_NAMES = [MyPagesRouteName.SETTINGS, ...SETTINGS_SECTIONS.map(section => section.routeName)]

/** @returns {SettingsSection | undefined} */
export function getSettingsSection (sectionId) {
  return SETTINGS_SECTIONS.find(section => section.id === sectionId)
}

/** Sections of one group, in display order. */
export function getSettingsSectionsForGroup (groupId) {
  return SETTINGS_SECTIONS.filter(section => section.group === groupId)
}

/** Whether the given route is the settings index or one of its pages (drives the drawer submenu). */
export function isSettingsRoute (routeName) {
  return SETTINGS_ROUTE_NAMES.includes(routeName)
}

export function getSettingsSectionTitleKey (sectionId) {
  return `mypages.settings.sections.${sectionId}.title`
}

export function getSettingsSectionDescriptionKey (sectionId) {
  return `mypages.settings.sections.${sectionId}.description`
}

export function getSettingsGroupTitleKey (groupId) {
  return `mypages.settings.index.groups.${groupId}`
}
